import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useValidatePostUrlMutation } from "@/pages/Private/redux/liPost/liPost.api";

import { ValidatePostUrlResponse } from "../schema/liPost";

export interface PostUrlValidation {
	postPreview: ValidatePostUrlResponse | null;
	validateError: string | null;
	isValidating: boolean;
	validate: (postUrl?: string) => Promise<void>;
	setPostPreview: Dispatch<SetStateAction<ValidatePostUrlResponse | null>>;
	resetPreview: () => void;
}

/** Manages the "validate post URL" call and its preview / error state. */
export const usePostUrlValidation = (): PostUrlValidation => {
	const { t } = useTranslation();
	const [validatePostUrl, { isLoading: isValidating }] = useValidatePostUrlMutation();
	const [postPreview, setPostPreview] = useState<ValidatePostUrlResponse | null>(null);
	const [validateError, setValidateError] = useState<string | null>(null);

	const resetPreview = useCallback(() => {
		setPostPreview(null);
		setValidateError(null);
	}, []);

	const validate = useCallback(
		async (postUrl?: string) => {
			if (!postUrl) {
				return;
			}

			setValidateError(null);
			setPostPreview(null);

			try {
				const result = await validatePostUrl({ postUrl }).unwrap();

				setPostPreview(result);
			} catch {
				setValidateError(t("liPost.validatePostUrlError"));
			}
		},
		[validatePostUrl, t]
	);

	return { postPreview, validateError, isValidating, validate, setPostPreview, resetPreview };
};
